/**
 * Roll Calculator Web Component
 * Calculates the number of wallpaper rolls needed based on wall dimensions.
 * Captures email and forwards to Zapier via Cloudflare Worker (App Proxy /apps/calculator/track).
 *
 * Logic from Shiny Step spec:
 * - Round up wall width to nearest 5
 * - Calculate: roundup_width × (height + raccord) / 10000 → round up = m²
 * - Rolls = m² / 5 → round up
 * - For 2 walls: add widths, take tallest height
 * - For >2 walls: show "contact us" message
 */
class RollCalculator extends HTMLElement {
  constructor() {
    super();
    this.raccord = 0;
    this.rollCoverage = 5;
    this.productType = '';
    this.contactEmail = 'contact@shinystep.com';
    this.productTitle = '';
    this.productUrl = '';
    this.productPrice = 0;
    this.lastResult = null;
  }

  connectedCallback() {
    this.raccord = parseFloat(this.dataset.raccord) || 0;
    this.rollCoverage = parseFloat(this.dataset.rollCoverage) || 5;
    this.productType = this.dataset.productType || '';
    this.contactEmail = this.dataset.contactEmail || 'contact@shinystep.com';
    this.productTitle = this.dataset.productTitle || '';
    this.productUrl = this.dataset.productUrl || '';
    this.productPrice = parseFloat(this.dataset.productPrice) || 0;

    // DOM references
    this.wallCountSelect = this.querySelector('[data-ref="wall-count"]');
    this.wall1Fields = this.querySelector('[data-ref="wall-1-fields"]');
    this.wall2Fields = this.querySelector('[data-ref="wall-2-fields"]');
    this.heightInput1 = this.querySelector('[data-ref="height-1"]');
    this.widthInput1 = this.querySelector('[data-ref="width-1"]');
    this.heightInput2 = this.querySelector('[data-ref="height-2"]');
    this.widthInput2 = this.querySelector('[data-ref="width-2"]');
    this.calculateBtn = this.querySelector('[data-ref="calculate-btn"]');
    this.resultContainer = this.querySelector('[data-ref="result"]');
    this.resultText = this.querySelector('[data-ref="result-text"]');
    this.moreWallsMessage = this.querySelector('[data-ref="more-walls-message"]');

    // Email capture elements
    this.emailSection = this.querySelector('[data-ref="email-section"]');
    this.emailInput = this.querySelector('[data-ref="email-input"]');
    this.firstNameInput = this.querySelector('[data-ref="firstname-input"]');
    this.emailSubmitBtn = this.querySelector('[data-ref="email-submit-btn"]');
    this.emailSuccess = this.querySelector('[data-ref="email-success"]');

    // Events
    this.wallCountSelect?.addEventListener('change', () => this.onWallCountChange());
    this.calculateBtn?.addEventListener('click', () => this.calculate());

    // Email + prénom — activent/désactivent le bouton calculer en temps réel
    this.emailInput?.addEventListener('input', () => this.updateCalculateBtnState());
    this.firstNameInput?.addEventListener('input', () => this.updateCalculateBtnState());

    // Allow Enter key to trigger calculation
    this.querySelectorAll('.roll-calculator__input').forEach((input) => {
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.calculate();
        }
      });
    });

    // Initialize state
    this.updateCalculateBtnState();
    this.onWallCountChange();
  }

  updateCalculateBtnState() {
    if (!this.calculateBtn) return;
    // Si pas de champ email (capture désactivée), le bouton est toujours actif
    if (!this.emailInput) {
      this.calculateBtn.disabled = false;
      return;
    }
    const email = this.emailInput.value?.trim() || '';
    const firstName = this.firstNameInput?.value?.trim() || '';
    this.calculateBtn.disabled = !this.isValidEmail(email) || firstName.length === 0;
  }

  onWallCountChange() {
    const wallCount = parseInt(this.wallCountSelect?.value) || 1;

    if (wallCount === 1) {
      this.wall2Fields?.classList.add('hidden');
      this.moreWallsMessage?.classList.add('hidden');
      this.wall1Fields?.classList.remove('hidden');
      this.calculateBtn?.classList.remove('hidden');
    } else if (wallCount === 2) {
      this.wall2Fields?.classList.remove('hidden');
      this.moreWallsMessage?.classList.add('hidden');
      this.wall1Fields?.classList.remove('hidden');
      this.calculateBtn?.classList.remove('hidden');
    } else {
      // 3+ walls
      this.wall2Fields?.classList.add('hidden');
      this.wall1Fields?.classList.add('hidden');
      this.calculateBtn?.classList.add('hidden');
      this.moreWallsMessage?.classList.remove('hidden');
    }

    this.resultContainer?.classList.add('hidden');
    this.emailSuccess?.classList.add('hidden');
  }

  /**
   * Round up to the nearest 5
   * e.g. 457 → 460, 460 → 460, 461 → 465
   */
  roundUpToFive(value) {
    return Math.ceil(value / 5) * 5;
  }

  calculate() {
    const wallCount = parseInt(this.wallCountSelect?.value) || 1;
    const height1 = parseFloat(this.heightInput1?.value) || 0;
    const width1 = parseFloat(this.widthInput1?.value) || 0;

    // Validation
    if (height1 <= 0 || width1 <= 0) {
      this.showError('Veuillez renseigner la hauteur et la largeur du mur.');
      return;
    }

    let totalWidth, maxHeight;

    if (wallCount === 2) {
      const height2 = parseFloat(this.heightInput2?.value) || 0;
      const width2 = parseFloat(this.widthInput2?.value) || 0;

      if (height2 <= 0 || width2 <= 0) {
        this.showError('Veuillez renseigner les dimensions du deuxième mur.');
        return;
      }

      // For 2 walls: add widths, take tallest height
      totalWidth = width1 + width2;
      maxHeight = Math.max(height1, height2);
    } else {
      totalWidth = width1;
      maxHeight = height1;
    }

    // Step 1: Round up width to nearest 5
    const roundedWidth = this.roundUpToFive(totalWidth);

    // Step 2: Calculate m² = roundedWidth × (height + raccord) / 10000
    const rawM2 = (roundedWidth * (maxHeight + this.raccord)) / 10000;
    const m2 = Math.ceil(rawM2);

    // Step 3: Rolls = m² / rollCoverage, rounded up
    const rolls = Math.ceil(m2 / this.rollCoverage);

    // Store result for email
    this.lastResult = { m2, rolls, totalWidth, maxHeight, wallCount };

    // Display result
    this.showResult(m2, rolls);

    // Update the quantity selector on the product page
    this.updateQuantitySelector(rolls);

    // Envoyer l'email + le résultat via App Proxy → Cloudflare Worker → Zapier (toujours actif)
    if (this.emailInput?.value) {
      this.submitEmail();
    }
  }

  showResult(m2, rolls) {
    if (!this.resultText || !this.resultContainer) return;

    const isPanoramique = this.productType === 'Papier peint panoramique';
    const pricePerUnit = this.productPrice / 100; // Shopify prices are in cents

    if (isPanoramique) {
      let html = `Vous avez besoin de <strong>${m2} m²</strong> de papier peint.`;
      if (pricePerUnit > 0) {
        const totalPrice = (m2 * pricePerUnit).toFixed(2).replace('.', ',');
        html += `<br><span class="roll-calculator__budget">Budget estimé : <strong>${totalPrice} €</strong></span>`;
      }
      this.resultText.innerHTML = html;
    } else {
      const rollLabel = rolls > 1 ? 'rouleaux' : 'rouleau';
      let html = `Vous avez besoin de <strong>${rolls} ${rollLabel}</strong> (${m2} m²).`;
      if (pricePerUnit > 0) {
        const totalPrice = (rolls * pricePerUnit).toFixed(2).replace('.', ',');
        html += `<br><span class="roll-calculator__budget">Budget estimé : <strong>${totalPrice} €</strong></span>`;
      }
      this.resultText.innerHTML = html;
    }

    this.resultContainer.classList.remove('hidden');
    this.resultContainer.classList.remove('roll-calculator__result--error');
    this.resultContainer.classList.add('roll-calculator__result--success');
  }

  showError(message) {
    if (!this.resultText || !this.resultContainer) return;

    this.resultText.innerHTML = message;
    this.resultContainer.classList.remove('hidden');
    this.resultContainer.classList.remove('roll-calculator__result--success');
    this.resultContainer.classList.add('roll-calculator__result--error');
    this.emailSection?.classList.add('hidden');
  }

  updateQuantitySelector(quantity) {
    // Find the quantity input on the product page
    const quantityInput = document.querySelector(
      'product-form input[name="quantity"], quantity-selector input[type="number"]'
    );
    if (quantityInput) {
      quantityInput.value = quantity;
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  async submitEmail() {
    const email = this.emailInput?.value?.trim();
    const firstName = this.firstNameInput?.value?.trim();

    if (!email || !this.isValidEmail(email)) {
      this.emailInput?.classList.add('roll-calculator__input--error');
      return;
    }
    this.emailInput?.classList.remove('roll-calculator__input--error');

    if (!firstName) {
      this.firstNameInput?.classList.add('roll-calculator__input--error');
      return;
    }
    this.firstNameInput?.classList.remove('roll-calculator__input--error');

    if (!this.lastResult) return;

    const isPanoramique = this.productType === 'Papier peint panoramique';
    const quantity = isPanoramique ? this.lastResult.m2 : this.lastResult.rolls;
    const unit = isPanoramique ? 'm²' : (quantity > 1 ? 'rouleaux' : 'rouleau');
    const pricePerUnit = this.productPrice / 100;
    const totalPrice = isPanoramique
      ? (this.lastResult.m2 * pricePerUnit)
      : (this.lastResult.rolls * pricePerUnit);

    const payload = {
      event: 'calculator_used',
      email,
      first_name: firstName || '',
      product_title: this.productTitle,
      product_type: this.productType,
      product_url: this.productUrl,
      quantity,
      unit,
      m2: this.lastResult.m2,
      rolls: this.lastResult.rolls,
      wall_count: this.lastResult.wallCount,
      budget_estimate: totalPrice.toFixed(2),
      price_per_unit: pricePerUnit.toFixed(2),
      timestamp: new Date().toISOString(),
    };

    // POST via Shopify App Proxy → Cloudflare Worker (HMAC-signed) → Zapier
    // L'URL est relative pour que Shopify intercepte et signe la requête.
    try {
      await fetch('/apps/calculator/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } catch (e) {
      console.warn('Calculator tracking failed', e);
      // On affiche quand même le succès au client — l'UX ne doit pas dépendre du tracking
    }

    this.emailSuccess?.classList.remove('hidden');
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }
}

if (!customElements.get('roll-calculator')) {
  customElements.define('roll-calculator', RollCalculator);
}
